import React from 'react';

import {Badge} from '@/components';
import {statusColors} from '@/types/Transaction';
import {formatDate} from '@/utils/date';

import type {VirtualCardInfoProps} from './types';

const VirtualCardInfo: React.FC<VirtualCardInfoProps> = ({
  cardData,
  virtualCardBalance,
}) => {
  const getBrandColor = (brand: string) => {
    switch (brand?.toLowerCase()) {
      case 'mastercard':
        return 'from-red-500 to-orange-500';
      case 'visa':
        return 'from-blue-500 to-indigo-600';
      case 'amex':
        return 'from-gray-600 to-gray-800';
      default:
        return 'from-purple-500 to-pink-500';
    }
  };

  if (!cardData) {
    return null;
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Card Visual */}
      <div className="rounded-2xl shadow-md w-full md:w-[400px]">
        {/* Virtual Card Design */}
        <div
          className={`relative w-full h-48 bg-gradient-to-br ${getBrandColor(cardData.info.brand)} rounded-2xl p-6 text-white shadow-md`}
        >
          <div className="flex justify-between items-start mb-8">
            <div className="text-sm opacity-90">VIRTUAL</div>
            <div className="text-right">
              <div className="text-xs opacity-75">{cardData.processor}</div>
              <div className="text-sm font-medium">{cardData.info.brand}</div>
            </div>
          </div>

          <div className="mb-4">
            <div className="text-lg font-mono tracking-wider">
              {`${cardData.info.first6} •••• •••• ${cardData.info.last4}`}
            </div>
          </div>

          <div className="flex justify-between items-end">
            <div>
              <div className="text-xs opacity-75">CARDHOLDER</div>
              <div className="text-sm font-medium">
                {cardData.info.name.fullName}
              </div>
            </div>
            <div className="flex justify-between gap-4">
              <div>
                <div className="text-xs opacity-75">STATUS</div>
                <div className="text-sm font-medium">
                  {cardData.info.isFrozen ? 'FROZEN' : 'ACTIVE'}
                </div>
              </div>
              <div className="">
                <div className="text-xs opacity-75">EXPIRES</div>
                <div className="text-sm font-medium">
                  {String(cardData.info.expiration.month).padStart(2, '0')}/
                  {String(cardData.info.expiration.year).slice(-2)}
                </div>
              </div>
            </div>
          </div>

          {/* Card chip */}
          <div className="absolute top-16 left-6 w-8 h-6 bg-gradient-to-br from-yellow-300 to-yellow-500 rounded opacity-80"></div>
        </div>
      </div>
      {/* Card Information - Consolidated */}

      <div className="space-y-2 mt-4">
        <div className="py-2 px-3 flex justify-between bg-gray-100 dark:bg-gray-900 rounded gap-3">
          <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-200">
            Balance
          </p>
          <p className="flex-[0.6] flex gap-2 text-sm font-medium w-1/2  truncate text-gray-700 dark:text-gray-100">
            {virtualCardBalance} {cardData.info.currency}
          </p>
        </div>
        <div className="py-2 px-3 flex justify-between bg-gray-100 dark:bg-gray-900 rounded gap-3">
          <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-200">
            Date Created
          </p>
          <p className="flex-[0.6] text-sm font-medium w-1/2 text-gray-700 dark:text-gray-100">
            {formatDate(cardData.createdAt as unknown as string)}
          </p>
        </div>
        <div className="py-2 px-3 flex justify-between bg-gray-100 dark:bg-gray-900 rounded gap-3">
          <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-200">
            Capabilities
          </p>
          <p className="flex-[0.6] flex gap-2 text-sm font-medium w-1/2  truncate text-gray-700 dark:text-gray-100">
            {cardData.capabilities.map((capability) => (
              <Badge
                label={capability}
                colorScheme={statusColors['disputed']}
                key={capability}
              />
            ))}
          </p>
        </div>
        <div className="py-2 px-3 flex justify-between bg-gray-100 dark:bg-gray-900 rounded gap-3">
          <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-200">
            Billing Address
          </p>
          <div className="flex-[0.6] text-sm font-medium w-1/2  truncate text-gray-700 dark:text-gray-100">
            <p>{cardData.info.locations.address.line1}</p>
            {cardData.info.locations.address.line2 && (
              <p>{cardData.info.locations.address.line2}</p>
            )}
            {cardData.info.locations.address.city},{' '}
            {cardData.info.locations.address.state},{' '}
            {cardData.info.locations.address.zip},{' '}
            {cardData.info.locations.address.country}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VirtualCardInfo;
